/**
 * Everything Mum Hub reads from and writes to the Worker, on the web (MAR-219).
 *
 * The mobile app splits this across five files because it also has StoreKit to
 * deal with. On the web there is one session mechanism, a cookie, and one
 * payment mechanism, a redirect to Stripe, so the endpoints live together and
 * share one transport.
 *
 * ## The session is a COOKIE, and that is the whole difference from mobile
 *
 * `mobile/src/lib/mum-hub/*` reads a bearer token out of the keychain and
 * sends `X-Client: mobile`, which is what makes the Worker answer SSO with a
 * token instead of a `Set-Cookie`. Web does neither. Every call here goes out
 * with `credentials: 'include'` and nothing else, exactly as
 * `client/src/lib/account-api.ts` does, and a request that forgets it is
 * indistinguishable from a signed-out one: a 401, on a page the customer has
 * paid for.
 *
 * ## Four refusals that mean four different things
 *
 * `GET /api/mum-hub/stages/:slug` has two gates in front of it and they must
 * not be collapsed:
 *
 * - 401, no valid session. Send her to sign in.
 * - 403, signed in and this account does not have Mum Hub. She must NOT be
 *   sent to sign in: re-authenticating as the same person fails identically,
 *   for ever, and a client that retries on this builds an infinite loop out of
 *   a settled answer.
 * - 404, the stage is not published. Not an error: Maria has not written it.
 * - 200 with no looks, published and empty. Different again, and the API keeps
 *   returning empty stages precisely so "published but empty" stays
 *   distinguishable from "not published".
 *
 * Each gets its own type, and {@link getMumHubStage} resolves a stage only on
 * a 200. Nothing here can turn a refusal into an empty stage, which would tell
 * a paying customer that Maria has written nothing.
 *
 * ## Privacy
 *
 * The due date is health-adjacent personal data. No error thrown here carries
 * it, and no response body is echoed into a message. The finest thing that
 * travels in a URL is a stage slug, which is one of four fixed values.
 */

import {
  type MumHubSilhouette,
  readSilhouette,
} from "@cch/shared/mum-hub-silhouettes";

import { API_BASE_URL } from "@/lib/constants";

import { readOfferCatalogue, type MumHubOfferEntry } from "./offers";
import { MUM_HUB_STAGE_SLUGS } from "./stages";

/** Anything that went wrong and is not one of the named refusals. */
export class MumHubApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "MumHubApiError";
    this.status = status;
  }
}

/** Signed out, or the session expired. The only refusal that offers sign-in. */
export class MumHubSignedOutError extends Error {
  constructor() {
    super("Mum Hub requires a signed-in session");
    this.name = "MumHubSignedOutError";
  }
}

/**
 * Signed in, and this account does not have Mum Hub.
 *
 * Distinct from {@link MumHubSignedOutError} because the correct response is
 * the opposite: say so plainly and stop. Never a sign-in prompt, never a
 * retry.
 */
export class MumHubNotEntitledError extends Error {
  constructor() {
    super("This account does not have Mum Hub");
    this.name = "MumHubNotEntitledError";
  }
}

/** The stage is not published yet. */
export class MumHubStageNotPublishedError extends Error {
  readonly slug: string;
  constructor(slug: string) {
    super("That Mum Hub stage is not published");
    this.name = "MumHubStageNotPublishedError";
    this.slug = slug;
  }
}

/**
 * The one transport. Test seam is the global `fetch`, which every other suite
 * in this repository stubs, so there is no module-level fetch to swap.
 */
async function mumHubFetch(path: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (cause) {
    throw new MumHubApiError(
      cause instanceof Error ? `Network error: ${cause.message}` : "Network error",
      0,
    );
  }
}

async function readJson(res: Response, what: string): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    throw new MumHubApiError(`The ${what} response was not valid JSON`, res.status);
  }
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Stage content
// ---------------------------------------------------------------------------

/** Which id space a piece lives in. The backoffice's `source`. */
export type MumHubItemSource = "catalogue" | "manual";

/**
 * The fields every piece carries, locked or not.
 *
 * These are exactly the fields the paywall preview keeps. The API's contract
 * is that a locked item still has its id, its position, its price and its id
 * space, and loses everything that would let a customer shop it. So the split
 * below is the wire format written as a type, and it is what lets "In 3
 * outfits" and every piece's own price stay true on a stage nobody has paid
 * for.
 */
export interface MumHubItemCommon {
  id: number;
  /**
   * Which id space this piece lives in. The backoffice populates `productId`
   * exactly when this is `catalogue` and `manualItemId` exactly when it is
   * `manual`, and the two spaces are unrelated: catalogue id 7 and manual id 7
   * are different things. That is why `capsule.ts` keys on the PAIR.
   */
  source: MumHubItemSource | null;
  /** Catalogue product id, or null for a manually added piece. */
  productId: string | null;
  /**
   * The backoffice's stable id for a manually added piece, or null. An INTEGER
   * on the wire, not a string, and stable across looks. Reading it as a string
   * returns null for every real value and sends every manual piece down the
   * row-id fallback, which silently undercounts the capsule.
   */
  manualItemId: number | null;
  /** Integer pence. Null when nobody has recorded a price. */
  pricePence: number | null;
  /**
   * The regular price in integer pence, while the piece is on sale. Null
   * otherwise, which is the ordinary case.
   *
   * Same convention as the shop catalogue: {@link pricePence} is what she pays
   * today and THIS is what it usually costs, so the pair is only ever a sale
   * when this one is the larger. {@link readOriginalPricePence} enforces that,
   * because a "sale" price that is not cheaper is a false discount claim and
   * renders perfectly.
   */
  originalPricePence: number | null;
  currency: string;
}

/** A piece a customer may look at in full. */
export interface MumHubUnlockedItem extends MumHubItemCommon {
  locked: false;
  name: string;
  brand: string | null;
  image: string | null;
  /**
   * Every photograph, the main one first, deduplicated, never empty when
   * `image` is set. The API sends `image` and a separate `images` list and the
   * two overlap.
   */
  images: string[];
  url: string | null;
  /**
   * Whether we have an affiliate arrangement with the shop this piece links
   * to, and therefore whether {@link url} goes through Skimlinks.
   *
   * Only `true` wraps. A missing field is `false`, which is the safe way
   * round for a reason worth stating: an unwrapped link to an affiliated shop
   * loses a commission, while a wrapped link to a shop we have no arrangement
   * with sends a customer through a redirect that earns nothing and discloses
   * a paid relationship that does not exist. The second is the one that is
   * wrong on the page rather than only in the accounts. Mamas & Papas is
   * affiliated; H&M UK is not.
   *
   * Optional on the type because older API responses predate the field, and
   * absent has to mean `false` rather than a type error. {@link readItem}
   * still resolves it to a definite boolean on the way past, so nothing
   * downstream has to hold a fallback of its own.
   */
  affiliate?: boolean;
  /** Maria's line about this specific piece. */
  note: string | null;
  /**
   * Per-size availability, or null when nobody has read the sizes.
   *
   * `null` is "not known" and is NOT an empty list. It means nobody has
   * checked, which is true of every piece that existed before the field did,
   * and it never means "no sizes are available". A size with `available:
   * false` is a size that exists and cannot be bought, which is a different
   * thing again from a size that was never stocked.
   */
  sizeOptions: MumHubSizeOption[] | null;
}

/**
 * A piece behind the paywall.
 *
 * Every field that could identify the garment is null BY CONSTRUCTION here
 * rather than by the server's good manners: {@link readItem} nulls them on the
 * way past even if the API sends them. A preview that leaked a name because
 * one endpoint forgot to blank it would be the whole feature failing quietly,
 * in the direction that costs money, and no test that renders it would notice
 * because the name would render correctly.
 *
 * The price SURVIVES, on purpose. It is what makes a locked tile an offer
 * rather than a redaction, and it is what keeps the arithmetic honest.
 */
export interface MumHubLockedItem extends MumHubItemCommon {
  locked: true;
  /**
   * What KIND of thing is behind the wash, so the tile can draw its outline.
   *
   * The one identifying thing a locked item keeps, and it is kept precisely
   * because it identifies nothing: "trousers" is true of several thousand
   * garments. It is what turns an empty wash into a piece she has not seen
   * yet.
   *
   * Never null. A missing field and a word this build does not know are both
   * `other`, resolved by {@link readSilhouette} on the way past, so no
   * surface holds a fallback of its own and none of them can disagree.
   */
  silhouette: MumHubSilhouette;
  name: null;
  brand: null;
  image: null;
  images: [];
  url: null;
  note: null;
  sizeOptions: null;
}

/**
 * A piece in a look. A union rather than a flag, so `if (item.locked)`
 * NARROWS: every surface that draws a name has to face the null, and the
 * compiler is what makes it.
 */
export type MumHubItem = MumHubUnlockedItem | MumHubLockedItem;

/**
 * One rung of the size ladder, exactly as the catalogue stores it. `size` is a
 * NUMBER: the backoffice coerces every entry with `Number(...)` before storing,
 * so a UK 8 arrives as `8` and never as `"8"`.
 */
export interface MumHubSizeOption {
  size: number;
  available: boolean;
}

/**
 * What kind of thing a look is.
 *
 * An outfit is clothes worn together and is what the capsule is made of. A kit
 * is a list of things to have rather than a way to be dressed: a hospital bag,
 * a feeding drawer. They are drawn differently, and a kit is excluded from the
 * capsule grid and from every outfit count.
 */
export type MumHubLookKind = "outfit" | "kit";

export interface MumHubLook {
  id: number;
  slug: string;
  /**
   * Outfit or kit. A missing or unrecognised `kind` reads as `'outfit'`, which
   * is not laziness: production answers this endpoint without the field for
   * older rows and every look in those is an outfit. Defaulting the other way
   * would empty the capsule of a whole live stage.
   */
  kind: MumHubLookKind;
  title: string;
  summary: string | null;
  /** Maria's note about the look as a whole. */
  guidance: string | null;
  /** The look's own photograph, or null. Null is common; always fall back. */
  heroImage: string | null;
  items: MumHubItem[];
}

/**
 * Whether this customer has bought this stage.
 *
 * Two booleans rather than one, because the backend sends two and they answer
 * different questions. `entitled` is "she has paid for this". `preview` is
 * "what you are holding has been blanked". They are not simply each other's
 * negation on the wire, and collapsing them here would mean guessing on the
 * day they disagree, on the page where guessing costs Maria a sale or gives
 * away the stage.
 */
export interface MumHubStageAccess {
  entitled: boolean;
  preview: boolean;
}

/**
 * What a payload with no `access` at all means: everything, unblanked.
 *
 * Defaulting the other way would put a paywall over the stage of every
 * entitled customer the moment an older payload arrived, which is the one
 * failure mode that is visible to a person and invisible to every test that
 * supplies an `access`.
 */
export const MUM_HUB_ACCESS_ENTITLED: MumHubStageAccess = {
  entitled: true,
  preview: false,
};

export interface MumHubStageContent {
  slug: string;
  title: string;
  intro: string | null;
  access: MumHubStageAccess;
  looks: MumHubLook[];
}

/**
 * Read `access`, or fall back to entitled. Exported so the fallback is
 * testable directly: it is the one rule here that has to keep working against
 * a payload that does not carry the field, and a regression in it is invisible
 * on any fixture that does.
 */
export function readStageAccess(raw: unknown): MumHubStageAccess {
  if (typeof raw !== "object" || raw === null) return MUM_HUB_ACCESS_ENTITLED;
  const row = raw as Record<string, unknown>;
  // Each field is read on its own and falls back on its own, so a payload that
  // carries one of the two is not thrown away wholesale.
  const entitled =
    typeof row.entitled === "boolean"
      ? row.entitled
      : MUM_HUB_ACCESS_ENTITLED.entitled;
  const preview =
    typeof row.preview === "boolean"
      ? row.preview
      : MUM_HUB_ACCESS_ENTITLED.preview;
  return { entitled, preview };
}

/**
 * `image` first, then `images`, strings only, each once.
 *
 * `images` may be null: the backend sends null rather than `[]` for a locked
 * item, and is not obliged to send an array for an unlocked one either. The
 * `Array.isArray` guard is what makes null mean "no photographs" instead of
 * throwing, and is why this takes `unknown`.
 */
function readImages(image: unknown, images: unknown): string[] {
  const list = [image, ...(Array.isArray(images) ? images : [])];
  return Array.from(
    new Set(list.filter((v): v is string => typeof v === "string" && v.length > 0)),
  );
}

/**
 * Which id space a piece lives in, or null if it cannot be told.
 *
 * Exported because the inference is the compatibility layer: older payloads
 * carry no `source` and every piece in them still has exactly one of the two
 * ids. Inferring from that is safe precisely because the backoffice populates
 * exactly one, so there is never a choice to get wrong. It refuses to name a
 * source for a piece with neither id, and the capsule then falls back to the
 * row id and undercounts openly rather than guessing.
 */
export function readItemSource(
  raw: unknown,
  productId: string | null,
  manualItemId: number | null,
): MumHubItemSource | null {
  if (raw === "catalogue" || raw === "manual") return raw;
  if (productId) return "catalogue";
  if (manualItemId !== null) return "manual";
  return null;
}

/**
 * One rung's size, or null if it is not one.
 *
 * A bare `Number(...)` is not safe here, which is the trap this function
 * exists to avoid. `Number(null)`, `Number('')`, `Number(false)` and
 * `Number([])` are all 0, and all finite, so a finiteness check waves them
 * through and a row of `{ size: null, available: true }` becomes rung 0, in
 * stock: a size that does not exist, marked buyable. Being trusted about stock
 * is this reader's whole job, and a wrong answer there is worse than a missing
 * one.
 *
 * Non-integer and non-positive rungs are refused. Checked against the data
 * rather than assumed: every `size` stored across the catalogue is a positive
 * integer between 2 and 28, so no real half size is lost.
 */
function readRungSize(raw: unknown): number | null {
  let candidate: number;
  if (typeof raw === "number") {
    candidate = raw;
  } else if (typeof raw === "string") {
    const text = raw.trim();
    if (!text) return null;
    candidate = Number(text);
  } else {
    return null;
  }
  // `Number.isInteger` is false for NaN and both infinities, so this covers
  // those too.
  if (!Number.isInteger(candidate) || candidate <= 0) return null;
  return candidate;
}

/**
 * Read the size ladder, or return null.
 *
 * Collapses an empty array to null deliberately: a ladder is never
 * legitimately empty, so `[]` on the wire is a shape we do not understand
 * rather than "no sizes", and rendering it as an empty size row would tell a
 * customer something nobody has established.
 */
export function readSizeOptions(raw: unknown): MumHubSizeOption[] | null {
  if (!Array.isArray(raw)) return null;
  const options: MumHubSizeOption[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    const size = readRungSize(row.size);
    if (size === null) continue;
    if (typeof row.available !== "boolean") continue;
    options.push({ size, available: row.available });
  }
  return options.length ? options : null;
}

/** Outfit or kit, defaulting to outfit. Exported so the default is testable. */
export function readLookKind(raw: unknown): MumHubLookKind {
  return raw === "kit" ? "kit" : "outfit";
}

/**
 * Read the was-price, or return null.
 *
 * Four different payloads have to come out as "not on sale", and only one of
 * them looks wrong on the way in: the field absent, the field null, a value
 * that is not a positive integer, and a value that is not GREATER than what
 * she pays. The last is the one worth a function. A struck-through £40 beside
 * a £40 price is a discount claim with no discount behind it, it renders
 * exactly as a real sale does, and it is the kind of thing the ASA and the
 * CMA read as misleading pricing. So the comparison lives here, once, and no
 * surface downstream is trusted to make it.
 *
 * With no price to be cheaper than, there is no sale either: a piece whose
 * `price_pence` did not read is not on sale, it is unpriced.
 */
export function readOriginalPricePence(
  raw: unknown,
  pricePence: number | null,
): number | null {
  if (pricePence === null) return null;
  const original = asInteger(raw);
  if (original === null || original <= 0) return null;
  return original > pricePence ? original : null;
}

/**
 * Read one piece, or drop it.
 *
 * A piece with no name cannot be drawn as anything a customer would recognise,
 * so it is dropped rather than rendered nameless. That mirrors the backoffice,
 * which drops a delisted product rather than emit a placeholder card: a gap in
 * a look is honest, a placeholder that looks like a product is not.
 */
export function readItem(raw: unknown): MumHubItem | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const id = asInteger(row.id);
  if (id === null) return null;
  const productId = asString(row.product_id);
  const manualItemId = asInteger(row.manual_item_id);
  const pricePence = asInteger(row.price_pence);
  const common: MumHubItemCommon = {
    id,
    source: readItemSource(row.source, productId, manualItemId),
    productId,
    manualItemId,
    pricePence: pricePence,
    originalPricePence: readOriginalPricePence(row.original_price_pence, pricePence),
    currency: asString(row.currency) ?? "GBP",
  };

  // `locked` is read as a strict `true` and NOT as truthiness: a missing field
  // is a shape production answers with, and every one of those items is hers.
  // Reading anything other than `true` as locked would blank a paying
  // customer's whole stage the moment a payload carried `locked: null`.
  if (row.locked === true) {
    // Blanked here rather than trusted from the wire. The API's contract is
    // that these are already null; this file does not rely on it, because a
    // leak in the other direction is unrecoverable and renders correctly.
    return {
      ...common,
      locked: true,
      silhouette: readSilhouette(row.silhouette),
      name: null,
      brand: null,
      image: null,
      images: [],
      url: null,
      note: null,
      sizeOptions: null,
    };
  }

  const name = asString(row.name);
  if (!name) return null;
  return {
    ...common,
    locked: false,
    name,
    brand: asString(row.brand),
    image: asString(row.image),
    images: readImages(row.image, row.images),
    url: asString(row.url),
    // Strict `true` only. Every other shape — absent, null, the string
    // "false", a 0 — is "we have no arrangement with this shop", which is the
    // reading that never claims a paid relationship we do not have.
    affiliate: row.affiliate === true,
    note: asString(row.note),
    sizeOptions: readSizeOptions(row.size_options),
  };
}

/** Read one look, or drop it. A look with no title has nothing to head it. */
function readLook(raw: unknown): MumHubLook | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const id = asInteger(row.id);
  const title = asString(row.title);
  if (id === null || !title) return null;
  const items = Array.isArray(row.items)
    ? row.items.map(readItem).filter((item): item is MumHubItem => item !== null)
    : [];
  return {
    id,
    slug: asString(row.slug) ?? String(id),
    kind: readLookKind(row.kind),
    title,
    summary: asString(row.summary),
    guidance: asString(row.guidance),
    heroImage: asString(row.hero_image),
    items,
  };
}

/**
 * Shape-check by hand rather than with Zod: a `ValidationError` would surface
 * the offending value, and this response carries Maria's unpublished words.
 */
export function readStageContent(data: unknown): MumHubStageContent {
  if (typeof data !== "object" || data === null) {
    throw new MumHubApiError("Mum Hub stage response was not an object", 200);
  }
  const row = data as Record<string, unknown>;
  const slug = asString(row.slug);
  const title = asString(row.title);
  if (!slug || !title) {
    throw new MumHubApiError("Mum Hub stage response had unexpected fields", 200);
  }
  const looks = Array.isArray(row.looks)
    ? row.looks.map(readLook).filter((look): look is MumHubLook => look !== null)
    : [];
  return {
    slug,
    title,
    intro: asString(row.intro),
    access: readStageAccess(row.access),
    looks,
  };
}

export const MUM_HUB_STAGE_QUERY_KEY = "mum-hub-stage";

/**
 * One stage's published content.
 *
 * A 200 is the only thing that produces a stage. Every refusal throws, and
 * each throws its own type, so no caller can render "not signed in", "not your
 * account" and "not written yet" as one another.
 */
export async function getMumHubStage(slug: string): Promise<MumHubStageContent> {
  const res = await mumHubFetch(`/api/mum-hub/stages/${encodeURIComponent(slug)}`);
  if (res.status === 401) throw new MumHubSignedOutError();
  if (res.status === 403) throw new MumHubNotEntitledError();
  if (res.status === 404) throw new MumHubStageNotPublishedError(slug);
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} for the Mum Hub stage`, res.status);
  }
  return readStageContent(await readJson(res, "Mum Hub stage"));
}

/**
 * Retry policy.
 *
 * Three of the four refusals are settled answers, and retrying them arrives at
 * the same place: a dead session will not revive, an account without Mum Hub
 * will not gain it mid-scroll, and an unpublished stage will not publish
 * itself between attempts. Only genuine faults retry.
 */
export function shouldRetryMumHubStage(failureCount: number, error: unknown): boolean {
  if (error instanceof MumHubSignedOutError) return false;
  if (error instanceof MumHubNotEntitledError) return false;
  if (error instanceof MumHubStageNotPublishedError) return false;
  return failureCount < 2;
}

// ---------------------------------------------------------------------------
// Entitlements
// ---------------------------------------------------------------------------

/**
 * Whether she paid for a stage or was given it (MAR-236).
 *
 * The question the page actually asks, and the one the old `source` field
 * answered only by accident. "Purchased on 9 March" and "Given by Maria" are a
 * decision about KIND, and reading it off a shop name means every new shop, and
 * every new way of granting a stage, has to be added to a condition somewhere
 * in the UI.
 */
export type MumHubEntitlementKind = "purchase" | "grant";

/**
 * Where a stage came from: which shop, or which way it was granted.
 *
 * Used for the small label beside a purchase, "on the website" or "in the
 * app", and for nothing else. Nothing branches on it to decide whether she
 * owns a thing or paid for it.
 */
export type MumHubEntitlementOrigin =
  | "app_store"
  | "web"
  | "admin"
  | "allowlist"
  | "unknown";

export interface MumHubStageEntitlement {
  /** Paid for, or given. The only field that decides what the page says. */
  kind: MumHubEntitlementKind;
  /**
   * Which shop, or which way it was granted.
   *
   * `unknown` is a real answer rather than a failure: a shop added to the
   * Worker after this build shipped is a place the page has no words for, and
   * the honest thing is to say nothing about where rather than name it wrongly
   * or throw the whole row away. She still owns the stage.
   */
  origin: MumHubEntitlementOrigin;
  purchasedAt: string;
  /**
   * When this stage's access ends, as an ISO timestamp, or null (MAR-247).
   *
   * Mum Hub is timed now: a stage lasts four months and the journey up to
   * twelve, less three for every whole trimester already behind her. Null means
   * there is no end date, which is a stage Maria granted by hand or one from
   * the allow-list. It never means "unknown", and the page draws nothing rather
   * than guessing at one.
   *
   * The Worker evaluates expiry when it reads, so an expired grant is simply
   * absent from the payload. Nothing here compares this with the clock.
   *
   * Optional on the type as well as on the wire: `readEntitlements` always sets
   * it, and a fixture about something else should not have to invent a date.
   */
  expiresAt?: string | null;
  /**
   * A Stripe receipt she can open, when the Worker sent one (MAR-236).
   *
   * Optional on the wire and read defensively: the backend only fills it in
   * when the request asked for receipts, it costs a Stripe call to produce, and
   * it only exists for a purchase made on the web: an App Store purchase has
   * no URL we can hand her at all. Absent means no link rather than a broken
   * one.
   */
  receiptUrl: string | null;
}

export interface MumHubEntitlements {
  all: boolean;
  /**
   * When the whole-journey grant ends, or null (MAR-247).
   *
   * Its own field because the journey is one grant covering four stages, and it
   * can differ from a stage's own date: an upgrader keeps the stage row she
   * bought first beside the journey row she bought later.
   */
  expiresAt?: string | null;
  /** Keyed by content slug, `trimester-1` and friends. */
  stages: Record<string, MumHubStageEntitlement>;
  /**
   * What she can still be sold, as Apple product ids. This is the list the
   * app renders; the website cannot price or check out by it.
   */
  offers: string[];
  /**
   * The same ladder as catalogue keys (`trimester1`, `journey`, ...), which is
   * what `web-config` prices and `/checkout` are keyed by. The paywall builds
   * its rows from THIS list: it was built from `offers` until 2026-09-06 and
   * matched no price, so every customer saw "Nothing to buy here".
   */
  offerKeys: string[];
  /**
   * The same ladder again, with everything needed to DRAW it (MAR-133).
   *
   * In display order, one entry per row, carrying what the row grants and how
   * long it lasts. The paywall is built from this and from nothing else, which
   * is what stopped this repository holding its own copy of the durations and
   * of which products exist: the two journey packs could not have been sold at
   * all from a list of keys typed in here.
   *
   * Optional on the type, like `expiresAt`: everything that came off the API
   * has it, and a fixture about something else need not invent a ladder.
   */
  offerCatalogue?: MumHubOfferEntry[];
  /**
   * Whether `offerCatalogue` was on the wire.
   *
   * An EMPTY ladder means there is nothing left to sell her; an ABSENT one
   * means a Worker that predates MAR-133 and the site derives a ladder from
   * `offerKeys` instead. Reading them the same way would resurrect rows the
   * Worker had deliberately withheld.
   */
  hasOfferCatalogue?: boolean;
  /**
   * The stage her due date puts her in today, as a content slug, or null when
   * she has not given one. Every stage from here on is still ahead of her.
   */
  stageToday?: string | null;
  /**
   * Whether `stageToday` is known rather than assumed.
   *
   * False means there is no usable due date on file and the Worker has treated
   * her as being at the start of the journey. A page may use this to ask for
   * her date; it must NOT use it to hide the offers.
   */
  stageKnown?: boolean;
}

export const MUM_HUB_NO_ENTITLEMENTS: MumHubEntitlements = {
  all: false,
  expiresAt: null,
  stages: {},
  offers: [],
  offerKeys: [],
  offerCatalogue: [],
  // A failed read is an OLD Worker as far as the ladder is concerned, which is
  // the harmless direction: it has no offer keys to derive one from either, so
  // both paths draw nothing.
  hasOfferCatalogue: false,
  stageToday: null,
  stageKnown: false,
};

export const MUM_HUB_ENTITLEMENTS_QUERY_KEY = ["mum-hub-entitlements"] as const;

/**
 * The receipts variant, under its OWN key (MAR-236).
 *
 * `?receipts=1` makes the Worker go to Stripe for a hosted receipt URL per
 * paid stage, which is slower than the plain read. The plain read is on the
 * hot path: the gate calls it on every page, the hub calls it, and the return
 * flow invalidates it. Sharing one cache entry would make all of them wait for
 * Stripe so that one page could show a link.
 */
export const MUM_HUB_ENTITLEMENTS_RECEIPTS_QUERY_KEY = [
  "mum-hub-entitlements",
  "receipts",
] as const;

const ENTITLEMENT_KINDS: readonly MumHubEntitlementKind[] = ["purchase", "grant"];

const ENTITLEMENT_ORIGINS: readonly MumHubEntitlementOrigin[] = [
  "app_store",
  "web",
  "admin",
  "allowlist",
  "unknown",
];

/**
 * One stage she owns, or null when the row says nothing this build can use.
 *
 * ## `kind` is what makes a row a row
 *
 * It used to be the legacy `source` field, and a row whose source this build
 * did not recognise was dropped. That was right when `source` was the only
 * field; the Worker no longer sends it, and dropping a row means telling a
 * paying customer she does not own a stage, which is the one mistake this whole
 * surface is built to avoid. So a readable `kind` is the whole test, and a row
 * without one is dropped: there is then nothing to draw, because every line on
 * the page is about having bought a stage or having been given one.
 *
 * An origin this build has no words for is `unknown` rather than a reason to
 * drop the row. She still owns the stage.
 */
function readStageEntitlement(raw: unknown): MumHubStageEntitlement | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  // Only a `kind` this build recognises counts. A `kind` of "complimentary"
  // from a Worker ahead of this one is not a string the page can branch on, so
  // the row is dropped rather than guessed at.
  const kind =
    typeof row.kind === "string" &&
    ENTITLEMENT_KINDS.includes(row.kind as MumHubEntitlementKind)
      ? (row.kind as MumHubEntitlementKind)
      : null;
  // Nothing on the page could describe this row.
  if (kind === null) return null;
  // An origin this build has no words for is `unknown`, not a reason to drop
  // her stage and not a reason to name a shop she may not have used.
  const origin: MumHubEntitlementOrigin =
    typeof row.origin === "string" &&
    ENTITLEMENT_ORIGINS.includes(row.origin as MumHubEntitlementOrigin)
      ? (row.origin as MumHubEntitlementOrigin)
      : "unknown";
  return {
    kind,
    origin,
    purchasedAt: typeof row.purchasedAt === "string" ? row.purchasedAt : "",
    // Absent, null, and anything that is not a non-empty string all mean the
    // same thing to the page: no end date to draw.
    expiresAt:
      typeof row.expiresAt === "string" && row.expiresAt.length > 0
        ? row.expiresAt
        : null,
    // Anything that is not a non-empty string is no receipt. A `false`, a
    // number or an absent key all mean the same thing to the page: no link.
    receiptUrl:
      typeof row.receiptUrl === "string" && row.receiptUrl.length > 0
        ? row.receiptUrl
        : null,
  };
}

export function readEntitlements(data: unknown): MumHubEntitlements {
  if (typeof data !== "object" || data === null) {
    throw new MumHubApiError("Mum Hub entitlements response was not an object", 200);
  }
  const row = data as Record<string, unknown>;
  const stages: Record<string, MumHubStageEntitlement> = {};
  if (typeof row.stages === "object" && row.stages !== null) {
    for (const [slug, value] of Object.entries(row.stages as object)) {
      const entitlement = readStageEntitlement(value);
      if (entitlement) stages[slug] = entitlement;
    }
  }
  const strings = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.filter((id): id is string => typeof id === "string" && id.length > 0)
      : [];
  // `all === true` and nothing else. A truthy string would otherwise unlock
  // every stage on a payload nobody meant to be permissive.
  return {
    all: row.all === true,
    // The journey's own end date, additive and nullable, so a Worker that has
    // not been deployed yet simply sends nothing and the page says nothing.
    expiresAt:
      typeof row.expiresAt === "string" && row.expiresAt.length > 0
        ? row.expiresAt
        : null,
    stages,
    offers: strings(row.offers),
    offerKeys: strings(row.offerKeys),
    offerCatalogue: readOfferCatalogue(row.offerCatalogue),
    // Present and an array, even an empty one, is a Worker that speaks the
    // catalogue. Anything else is one that does not.
    hasOfferCatalogue: Array.isArray(row.offerCatalogue),
    stageToday:
      typeof row.stageToday === "string" &&
      MUM_HUB_STAGE_SLUGS.includes(row.stageToday)
        ? row.stageToday
        : null,
    // `true` and nothing else, and never inferred from `stageToday` being
    // present: the two are separate facts on the wire, and a build that guessed
    // one from the other would call an assumed stage a known one.
    stageKnown: row.stageKnown === true,
  };
}

export async function getMumHubEntitlements(
  { receipts = false } = {},
): Promise<MumHubEntitlements> {
  const res = await mumHubFetch(
    receipts ? "/api/mum-hub/entitlements?receipts=1" : "/api/mum-hub/entitlements",
  );
  if (res.status === 401) throw new MumHubSignedOutError();
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} for Mum Hub entitlements`, res.status);
  }
  return readEntitlements(await readJson(res, "Mum Hub entitlements"));
}

// ---------------------------------------------------------------------------
// Profile: the due date
// ---------------------------------------------------------------------------

export interface MumHubProfile {
  dueDate: string | null;
  dueDatePrecision: "day" | "month" | null;
}

export interface MumHubProfileInput {
  dueDate: string;
  dueDatePrecision: "day" | "month";
}

export const MUM_HUB_PROFILE_QUERY_KEY = ["mum-hub-profile"] as const;

const PROFILE_PATH = "/api/account/me/mum-hub-profile";

export function readProfile(data: unknown): MumHubProfile {
  if (typeof data !== "object" || data === null) {
    throw new MumHubApiError("Mum Hub profile response was not an object", 200);
  }
  const record = data as Record<string, unknown>;
  const dueDate = record.dueDate;
  const precision = record.dueDatePrecision;
  const dueDateOk = dueDate === null || typeof dueDate === "string";
  const precisionOk =
    precision === null ||
    precision === undefined ||
    precision === "day" ||
    precision === "month";
  if (!dueDateOk || !precisionOk) {
    throw new MumHubApiError("Mum Hub profile response had unexpected fields", 200);
  }
  return {
    dueDate: (dueDate as string | null) ?? null,
    dueDatePrecision: (precision as "day" | "month" | null) ?? null,
  };
}

export async function getMumHubProfile(): Promise<MumHubProfile> {
  const res = await mumHubFetch(PROFILE_PATH);
  if (res.status === 401) throw new MumHubSignedOutError();
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} for the Mum Hub profile`, res.status);
  }
  return readProfile(await readJson(res, "Mum Hub profile"));
}

/**
 * Save the date.
 *
 * Both guards run BEFORE the request, and they are the same two rules the
 * Worker enforces. A form that can compose an out-of-range date or a
 * month-precision date that is not the 1st gets a 400 with a body it must not
 * show the customer; refusing here means the message she reads is one we wrote.
 */
export async function putMumHubProfile(
  input: MumHubProfileInput,
  now: Date,
  guards: {
    isPrecisionConsistent: (date: string, precision: "day" | "month") => boolean;
    isDueDateWithinBounds: (date: string, now: Date) => boolean;
  },
): Promise<MumHubProfile> {
  if (!guards.isPrecisionConsistent(input.dueDate, input.dueDatePrecision)) {
    throw new MumHubApiError("Mum Hub due date and precision disagree", 0);
  }
  if (!guards.isDueDateWithinBounds(input.dueDate, now)) {
    throw new MumHubApiError("Mum Hub due date is out of range", 0);
  }
  const res = await mumHubFetch(PROFILE_PATH, {
    method: "PUT",
    body: JSON.stringify({
      dueDate: input.dueDate,
      dueDatePrecision: input.dueDatePrecision,
    }),
  });
  if (res.status === 401) throw new MumHubSignedOutError();
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} saving the Mum Hub profile`, res.status);
  }
  try {
    return readProfile(await res.json());
  } catch (cause) {
    if (cause instanceof MumHubApiError && cause.status === 200) throw cause;
    // A 204 or an empty body is a success with nothing to read back, and what
    // we just sent is what is now stored.
    return { dueDate: input.dueDate, dueDatePrecision: input.dueDatePrecision };
  }
}

export async function deleteMumHubProfile(): Promise<MumHubProfile> {
  const res = await mumHubFetch(PROFILE_PATH, { method: "DELETE" });
  if (res.status === 401) throw new MumHubSignedOutError();
  if (!res.ok && res.status !== 204) {
    throw new MumHubApiError(
      `HTTP ${res.status} removing the Mum Hub profile`,
      res.status,
    );
  }
  return { dueDate: null, dueDatePrecision: null };
}

// ---------------------------------------------------------------------------
// Web config, checkout and the return from Stripe
// ---------------------------------------------------------------------------

export interface MumHubPrice {
  amountPence: number;
  currency: string;
}

/**
 * What the web half of Mum Hub is allowed to sell today.
 *
 * `enabled` is the BACKEND's switch and is not the same thing as this repo's
 * feature flag. The flag decides whether the pages exist at all; this decides
 * whether they may take money. They are separate because they fail in
 * different directions: shipping the pages with no Stripe products configured
 * must show "not open yet", not a button that 400s.
 */
export interface MumHubWebConfig {
  enabled: boolean;
  prices: Partial<Record<string, MumHubPrice>>;
}

export const MUM_HUB_WEB_CONFIG_QUERY_KEY = ["mum-hub-web-config"] as const;

/**
 * Nothing for sale.
 *
 * The default when the endpoint is missing, unreachable or malformed, and it
 * is deliberately the CLOSED direction, the opposite of `readStageAccess`.
 * Those two defaults look inconsistent and are not: guessing "entitled" shows
 * a paying customer her own stage, and guessing "enabled" shows everyone a buy
 * button that cannot complete a purchase.
 */
export const MUM_HUB_WEB_CLOSED: MumHubWebConfig = { enabled: false, prices: {} };

function readPrice(raw: unknown): MumHubPrice | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const amountPence = asInteger(row.amountPence);
  const currency = asString(row.currency);
  // A zero or negative price is not a free stage, it is a misconfigured Stripe
  // product, and printing "£0" beside "Unlock" is worse than printing nothing.
  if (amountPence === null || amountPence <= 0 || !currency) return null;
  return { amountPence, currency };
}

export function readWebConfig(data: unknown): MumHubWebConfig {
  if (typeof data !== "object" || data === null) return MUM_HUB_WEB_CLOSED;
  const row = data as Record<string, unknown>;
  const prices: Record<string, MumHubPrice> = {};
  if (typeof row.prices === "object" && row.prices !== null) {
    for (const [key, value] of Object.entries(row.prices as object)) {
      const price = readPrice(value);
      if (price) prices[key] = price;
    }
  }
  return { enabled: row.enabled === true, prices };
}

export async function getMumHubWebConfig(): Promise<MumHubWebConfig> {
  const res = await mumHubFetch("/api/mum-hub/web-config");
  // A 404 is the shape of a Worker that has not deployed the Stripe half yet,
  // and it is a legitimate "not open" rather than a fault worth retrying.
  if (res.status === 404) return MUM_HUB_WEB_CLOSED;
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} for the Mum Hub web config`, res.status);
  }
  return readWebConfig(await readJson(res, "Mum Hub web config"));
}

/** Why the Worker refused to open a checkout. */
export type MumHubCheckoutRefusal =
  | "unknown_product"
  | "already_owned"
  | "bad_return_path"
  | "stripe_unavailable"
  | "not_eligible"
  | "unknown";

const CHECKOUT_REFUSALS: readonly MumHubCheckoutRefusal[] = [
  "unknown_product",
  "already_owned",
  "bad_return_path",
  "stripe_unavailable",
  "not_eligible",
];

export class MumHubCheckoutRefusedError extends Error {
  readonly reason: MumHubCheckoutRefusal;
  constructor(reason: MumHubCheckoutRefusal) {
    super(`The Mum Hub checkout was refused: ${reason}`);
    this.name = "MumHubCheckoutRefusedError";
    this.reason = reason;
  }
}

export function readCheckoutRefusal(raw: unknown): MumHubCheckoutRefusal {
  if (typeof raw !== "string") return "unknown";
  return CHECKOUT_REFUSALS.includes(raw as MumHubCheckoutRefusal)
    ? (raw as MumHubCheckoutRefusal)
    : "unknown";
}

/**
 * Open a Stripe Checkout session and hand back the URL to send her to.
 *
 * This function does NOT navigate. The caller does, so that a test can assert
 * on the URL without the jsdom navigation warning, and so the one place that
 * changes `window.location` is visible in the component.
 *
 * `returnPath` must be a site-relative path. The Worker validates it and
 * answers `bad_return_path` rather than trusting us, because it becomes the
 * `success_url` on the Stripe session and an absolute URL there is an open
 * redirect with a payment attached.
 */
export async function startMumHubCheckout(
  product: string,
  returnPath: string,
): Promise<string> {
  const res = await mumHubFetch("/api/mum-hub/checkout", {
    method: "POST",
    body: JSON.stringify({ product, returnPath }),
  });
  if (res.status === 401) throw new MumHubSignedOutError();
  if (res.status === 400 || res.status === 409) {
    let reason: unknown;
    try {
      reason = ((await res.json()) as Record<string, unknown>)?.reason;
    } catch {
      reason = undefined;
    }
    throw new MumHubCheckoutRefusedError(readCheckoutRefusal(reason));
  }
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} opening the checkout`, res.status);
  }
  const body = await readJson(res, "Mum Hub checkout");
  const url =
    typeof body === "object" && body !== null
      ? asString((body as Record<string, unknown>).url)
      : null;
  if (!url) {
    throw new MumHubApiError("The checkout response carried no URL", res.status);
  }
  return url;
}

export type MumHubCheckoutStatus = "paid" | "open" | "expired";

export interface MumHubCheckoutResult {
  status: MumHubCheckoutStatus;
  entitlements: MumHubEntitlements;
}

/**
 * Where a Stripe session has got to.
 *
 * `open` means Stripe has taken the payment and the webhook has not landed
 * yet, which is the ordinary case for the first second or two after she comes
 * back, and is why the page polls rather than reading once.
 */
export function readCheckoutResult(data: unknown): MumHubCheckoutResult {
  if (typeof data !== "object" || data === null) {
    throw new MumHubApiError("Mum Hub checkout status was not an object", 200);
  }
  const row = data as Record<string, unknown>;
  const status = row.status;
  if (status !== "paid" && status !== "open" && status !== "expired") {
    throw new MumHubApiError("Mum Hub checkout status was not recognised", 200);
  }
  // Entitlements may legitimately be absent while the session is still open.
  let entitlements = MUM_HUB_NO_ENTITLEMENTS;
  if (typeof row.entitlements === "object" && row.entitlements !== null) {
    entitlements = readEntitlements(row.entitlements);
  }
  return { status, entitlements };
}

export async function getMumHubCheckout(
  sessionId: string,
): Promise<MumHubCheckoutResult> {
  const res = await mumHubFetch(
    `/api/mum-hub/checkout/${encodeURIComponent(sessionId)}`,
  );
  if (res.status === 401) throw new MumHubSignedOutError();
  if (!res.ok) {
    throw new MumHubApiError(`HTTP ${res.status} for the checkout status`, res.status);
  }
  return readCheckoutResult(await readJson(res, "Mum Hub checkout status"));
}
